import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { BehaviorSubject, interval, Subject, Subscription } from 'rxjs';
import { debounce, takeUntil } from 'rxjs/operators';
import { LS } from 'src/app/helpers/localStorage.enum';
import { AnnotationService } from 'src/app/services/annotation/annotation.service';
import { DataService } from 'src/app/services/data/data.service';
import { UserService } from 'src/app/services/user/user.service';
import { LocationStoreService } from 'src/app/stores/location-store/location-store.service';
import { GroupService } from 'src/app/services/group/group.service'
import Fuse from 'fuse.js'

@Component({
  selector: 'app-task-form',
  templateUrl: './task-form.component.html',
  styleUrls: ['./task-form.component.sass']
})
export class TaskFormComponent implements OnInit, OnDestroy {

  @Input('task') taskInput: ITaskForm;
  @Input() inAnnote: boolean = false;

  /** taskInput and onValueChange can be assigned to the same variable. See sample in create-task.html */
  @Output() onValueChanges = new EventEmitter<ITaskForm>()
  /** Assign this to a boolean to retrieve status */
  @Output() validForm = new EventEmitter<boolean>()
  /** Gets emitted when enter is pressed */
  @Output() onSubmit = new EventEmitter<ITaskForm>()

  get formAnnot() {
    return this.taskForm.controls['annotation']
  }
  
  get formName() {
    return this.taskForm.controls['name']
  }

  get formDate() {
    return this.taskForm.controls['fixing_date']
  }

  get formDescription() {
    return this.taskForm.controls['description']
  }

  get formAssignee() {
    return this.taskForm.controls['assignee']
  }

  taskForm: FormGroup = this.formBuilder.group({
    name: ['', Validators.required],
    description: ['', Validators.required],
    fixing_date: ['', Validators.required],
    assignee: ['', Validators.required],
    assigner: [''],
    annotation: ['', Validators.required],
    location: ['']
  })

  unsubber: Subject<boolean> = new Subject<boolean>()
  userServiceSub: Subscription;
  currentUser: any;
  userSelection: any;
  userSelected: any;
  annotations: any;
  location: any;
  userSearchData: any;
  
  assigneeEmail = ''

  constructor(
    private formBuilder: FormBuilder,
    private userService: UserService,
    private annotationService: AnnotationService,
    private dataService: DataService,
    private locationStore: LocationStoreService,
    private groupService: GroupService
  ) { }

  ngOnInit(): void {
    this.locationStore.currLocation$.subscribe((res) => {
      this.location = res.id

      this.annotationService.findAll([
        { key: 'filtered[0][key]', val: 'location' },
        { key: 'filtered[0][val]', val: res.id }
      ]).subscribe(res => {
        this.annotations = res.data;
      })
    })
    this.currentUser = JSON.parse(localStorage.getItem(LS.currentUser));
    this.taskForm.valueChanges.pipe(
        debounce(() => interval(300)),
        takeUntil(this.unsubber.asObservable())
      )
      .subscribe(form => {
        this.onValueChanges.emit(form)
        this.validForm.emit(this.taskForm.valid)
      })
    this.populateForm()

    this.userService.findAssignee().pipe(takeUntil(this.unsubber)).subscribe((res)=>{
      this.userSearchData = res
    })
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.taskInput && changes.taskInput.currentValue) {
      if (!this.taskInput) return
      if (typeof(this.taskInput?.assignee) == 'object') {
        this.userSelected = this.taskInput.assignee
        this.assigneeEmail = this.userSelected.email
      }
      this.populateForm(this.taskInput)
    }
  }

  ngOnDestroy(): void {
    this.unsubber.next(true)
    this.unsubber.unsubscribe()
  }

  populateForm(values?: ITaskForm) {
    this.taskForm.setValue({
      name: values?.name || '',
      description: values?.description || '',
      fixing_date: values?.fixing_date || '',
      assignee: values?.assignee || '',
      assigner: values?.assigner || '',
      annotation: values?.annotation || '',
      location: this.location || ''
    })
  }

  onFormSubmit() {
    const form = this.taskForm.getRawValue()
    if (!this.userSelected) {
      delete form.assignee
      delete form.assigner
    }
    form.location = this.location
    this.onSubmit.emit(form)
  }

  searchUser($event) {
    if ($event.target.value.length > 0) {
        const fuse = new Fuse(this.userSearchData, {threshold: 0.2, keys: ['email']})
        const searched = fuse.search($event.target.value)
        this.userSelection = searched.slice(0, 5)
    } else {
      this.userSelection = []
    }
  }

  selectedUser(user) {
    this.userSelected = user
    this.assigneeEmail = user.email
    this.taskForm.patchValue({
      assignee: this.userSelected.id,
      assigner: this.currentUser.user_id
    })
    this.userSelection = []
  }

}

export interface ITaskForm {
  id?: string
  name: string
  description?: string
  fixing_date: string
  annotation?: any
  location?: any
  assignee?: string[]
  assigner?: string
  created_at?: string
  updated_at?: string
}
